import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import get from 'lodash/get';
import { connect } from 'react-redux';
import { styled } from '@mui/system';
import muiPropForwarding from 'styles/helpers/muiPropForwarding';

import Cart from 'bento-ordering/cart/components/Cart';
import { isMobileMedia } from 'bento-ordering/utils/media';
import { generatePath } from 'bento-ordering/utils/generatePath';
import {
  clearCart,
  removeCartItem,
  removeCartItemFromRedux
} from 'bento-ordering/cart/actions/index';

import { CART } from 'bento-ordering/cart/constants/entities';
import ConfirmContainer from 'bento-ordering/base/containers/ConfirmContainer';
import CartConfirmModal from 'bento-ordering/cart/components/CartConfirmModal';
import {
  isSubtotalBelowMinimumSelector,
  getFulfillmentTimeOrASAPDisplaySelector
} from 'bento-ordering/cart/selectors';
import { getCartMinimumSelector } from 'bento-ordering/account/reducers';

import withLoading from 'bento-ordering/base/hoc/withLoading';
import LaunchNotificationOnMount from 'bento-ordering/base/containers/LaunchNotificationOnMount';
import EditSnackbar from 'bento-ordering/base/components/Snackbar/EditSnackbar';
import { resetLastItem } from 'bento-ordering/cart/actions/lastItem';
import { getItemByIdSelector } from 'bento-ordering/base/reducers/bentoApi';
import {
  goToMenu,
  goToOrderSettings,
  goToLocationListPage
} from 'bento-ordering/cart/actions/orderSettings';

import validateCart from 'bento-ordering/cart/actions/validateCart';
import isDineInApplicationVariant from 'bento-ordering/utils/isDineInApplicationVariant';
import { selectIsUpsellItemAddedFromCart } from 'bento-ordering/cart/utils/menuUpsellSelectors';
import SuccessfulUpsellAddSnackbar from 'bento-ordering/base/components/Snackbar/SuccessfulUpsellAddSnackbar';
import { LayoutContext } from 'bento-ordering/context/LayoutContext';

const StyledContainer = styled('div', {
  shouldForwardProp: muiPropForwarding('bottomNavHeight')
})(({ bottomNavHeight }) => ({
  padding: `20px 20px ${bottomNavHeight || 0}px 20px`
}));

class CartContainer extends React.Component {
  static contextType = LayoutContext;
  static propTypes = {
    cart: PropTypes.object,
    history: PropTypes.shape({ push: PropTypes.func, goBack: PropTypes.func }),
    dispatch: PropTypes.func,
    currentPrepTime: PropTypes.number,
    locationDetail: PropTypes.object,
    restaurantName: PropTypes.string,
    getIsExpired: PropTypes.func,
    location: PropTypes.object,
    lastItemEdited: PropTypes.shape({
      product: PropTypes.number,
      name: PropTypes.string
    }).isRequired,
    match: PropTypes.shape({
      url: PropTypes.string
    }),
    selectedLocationSlug: PropTypes.string,
    isTotalBelowMinimum: PropTypes.bool,
    orderMinimum: PropTypes.number,
    fulfillmentAddress: PropTypes.string,
    fulfillmentTimeOrASAP: PropTypes.string,
    fulfillmentMethod: PropTypes.string,
    isDeliveryWithNoAddress: PropTypes.bool,
    deliveryFee: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    customFee: PropTypes.string,
    customFeeName: PropTypes.string,
    bundleServiceFeeWithCustomFee: PropTypes.bool,
    bundledServiceAndCustomFees: PropTypes.string,
    previousLocation: PropTypes.object.isRequired,
    digitalGiftCardAmountApplied: PropTypes.number.isRequired,
    covidFee: PropTypes.number.isRequired,
    tipAmount: PropTypes.string.isRequired,
    lastItemAdded: PropTypes.shape({
      product: PropTypes.number,
      name: PropTypes.string
    }).isRequired,
    isLastItemAddedUpsell: PropTypes.bool.isRequired
  };

  constructor(props) {
    super(props);

    this.cartRef = React.createRef();
    this.isMobile = isMobileMedia();
    this.state = {
      showUpsellSnackbar: false
    };
  }

  componentDidMount() {
    const { dispatch } = this.props;
    if (!this.isCartEmpty()) {
      dispatch(validateCart());
    }
  }

  isCartEmpty() {
    return get(this.props.cart, 'items', []).length === 0;
  }

  componentDidUpdate(prevProps) {
    const { cart } = prevProps;
    const { lastItemAdded = {}, isLastItemAddedUpsell } = this.props;
    const { showUpsellSnackbar } = this.state;

    if (get(cart, 'items', []).length !== 0 && this.isCartEmpty()) {
      this.clearCart();
    }

    if (!!lastItemAdded.name && isLastItemAddedUpsell && !showUpsellSnackbar) {
      // If upsell menu item was added and snackbar is not open
      this.setState({ showUpsellSnackbar: true });
    } else if (!isLastItemAddedUpsell && showUpsellSnackbar) {
      // If snackbar was closed (last item reset) we reset snackbar state
      this.setState({ showUpsellSnackbar: false });
    }
  }

  goToOrderSettings = () => {
    this.props.dispatch(goToOrderSettings());
  };

  handleClose = () => {
    this.navigateToMenu();
  };

  navigateToMenu = () => {
    this.props.dispatch(goToMenu());
  };

  navigateToPreviousLocation = () => {
    if (this.props.previousLocation.pathname === '/') {
      this.props.dispatch(goToLocationListPage());
    } else {
      this.props.dispatch(goToMenu());
    }
  };

  clearCart = () => {
    this.props.dispatch(clearCart());
    this.handleClose();
  };

  getEditCartItemPath = cartItemId => {
    return generatePath(CART, 'cartItem', {
      cartItemId
    });
  };

  removeItemFromCart = async id => {
    const { dispatch, cart } = this.props;
    const result = await dispatch(removeCartItem(id));

    if (result.payload.status === 404) {
      const updatedCartItems = cart.items.filter(item => item.id !== id);
      dispatch(removeCartItemFromRedux(updatedCartItems));
      this.handleClose();
    } else if (get(result, 'payload.cart.items', []).length === 0) {
      this.handleClose();
    } else if (this.state.showUpsellSnackbar) {
      /** This condition was added to address the edge case when
       * an upsell cart item is being removed while the upsell snackbar is still open.
       */
      this.handleSnackbarClose();
    }
  };

  handleSnackbarClose = () => {
    const { dispatch } = this.props;

    dispatch(resetLastItem());
  };

  render() {
    const {
      cart,
      restaurantName,
      currentPrepTime,
      match,
      lastItemEdited,
      dispatch,
      isTotalBelowMinimum,
      orderMinimum,
      fulfillmentAddress,
      fulfillmentTimeOrASAP,
      fulfillmentMethod,
      deliveryFee,
      customFee,
      customFeeName,
      bundleServiceFeeWithCustomFee,
      bundledServiceAndCustomFees,
      digitalGiftCardAmountApplied,
      covidFee,
      tipAmount,
      lastItemAdded,
      isLastItemAddedUpsell
    } = this.props;
    const isDineInVariant = isDineInApplicationVariant();
    const disableCheckoutButton = this.isCartEmpty();

    const { bottomNavHeight } = this.context;

    return (
      <StyledContainer bottomNavHeight={bottomNavHeight}>
        <LaunchNotificationOnMount handleCallback={this.handleClose} />
        <ConfirmContainer>
          {(isOpen, modalType, toggleIsOpen) => (
            <React.Fragment>
              {!isOpen && (
                <Cart
                  {...cart}
                  ref={this.cartRef}
                  restaurantName={restaurantName}
                  handleClose={this.navigateToPreviousLocation}
                  handleEditClick={this.handleEditClick}
                  currentPrepTime={currentPrepTime}
                  toggleIsOpen={toggleIsOpen}
                  getEditCartItemPath={this.getEditCartItemPath}
                  removeItemFromCart={this.removeItemFromCart}
                  currentCartPath={match.url}
                  changeFulfillment={
                    // Order settings is not available for dine-in
                    isDineInVariant ? null : this.goToOrderSettings
                  }
                  isTotalBelowMinimum={isTotalBelowMinimum}
                  orderMinimum={orderMinimum}
                  fulfillmentAddress={fulfillmentAddress}
                  fulfillmentTimeOrASAP={fulfillmentTimeOrASAP}
                  fulfillmentMethod={fulfillmentMethod}
                  deliveryFee={deliveryFee}
                  customFee={customFee}
                  customFeeName={customFeeName}
                  bundleServiceFeeWithCustomFee={bundleServiceFeeWithCustomFee}
                  bundledServiceAndCustomFees={bundledServiceAndCustomFees}
                  digitalGiftCardAmountApplied={digitalGiftCardAmountApplied}
                  covidFee={covidFee}
                  tipAmount={tipAmount}
                  lastItemAdded={lastItemAdded}
                  isLastItemAddedUpsell={isLastItemAddedUpsell}
                  disableCheckoutButton={disableCheckoutButton}
                />
              )}
              {isOpen && (
                <CartConfirmModal
                  toggleIsOpen={toggleIsOpen}
                  modalType={modalType}
                />
              )}
            </React.Fragment>
          )}
        </ConfirmContainer>
        <EditSnackbar
          open={!!lastItemEdited.name}
          item={lastItemEdited.name}
          onClose={() => dispatch(resetLastItem())}
        />
        {this.state.showUpsellSnackbar && (
          <SuccessfulUpsellAddSnackbar
            item={lastItemAdded.name}
            handleSnackbarClose={this.handleSnackbarClose}
          />
        )}
      </StyledContainer>
    );
  }
}

const mapStateToProps = state => {
  const {
    cart,
    restaurantLocations,
    lastItem,
    locationThrottledTimes,
    menu,
    router
  } = state;
  const selectedLocation = getItemByIdSelector(
    state.restaurantLocations,
    cart.online_ordering_location
  );

  const {
    online_ordering_fulfillment_method,
    online_ordering_delivery_fee,
    shipping_street_address_1,
    online_ordering_custom_fee,
    online_ordering_custom_fee_name,
    bundle_service_fee_with_custom_fee,
    bundled_service_and_custom_fees,
    digital_gift_card_amount_applied,
    online_ordering_covid_fee,
    tip_amount
  } = cart;
  const orderMinimum = getCartMinimumSelector(cart);
  const isTotalBelowMinimum = isSubtotalBelowMinimumSelector(cart);
  const fulfillmentTimeOrASAP = getFulfillmentTimeOrASAPDisplaySelector({
    restaurantLocations,
    cart,
    locationThrottledTimes
  });
  const isLastItemAddedUpsell = selectIsUpsellItemAddedFromCart(
    lastItem,
    menu,
    router
  );

  return {
    cart,
    restaurantName: get(selectedLocation, 'location.name'),
    selectedLocationSlug: get(selectedLocation, 'location.slug'),
    currentPrepTime: selectedLocation.current_prep_time,
    lastItemEdited: lastItem.edit,
    lastItemAdded: lastItem.add,
    isTotalBelowMinimum,
    orderMinimum,
    fulfillmentTimeOrASAP,
    /**
     * 12/20/2019 TODO: shipping_street_address_1 always gets set base on
     * the restaurant address which is a logic flaw because the user
     * will always have a delivery address even if it may be the
     * restaurant address. ECOM-980 will handle this fix and this
     * comment can be removed.
     */
    fulfillmentAddress: shipping_street_address_1,
    fulfillmentMethod: online_ordering_fulfillment_method,
    deliveryFee: online_ordering_delivery_fee,
    customFee: online_ordering_custom_fee,
    customFeeName: online_ordering_custom_fee_name,
    bundleServiceFeeWithCustomFee: bundle_service_fee_with_custom_fee,
    bundledServiceAndCustomFees: bundled_service_and_custom_fees,
    digitalGiftCardAmountApplied: digital_gift_card_amount_applied,
    covidFee: online_ordering_covid_fee,
    tipAmount: tip_amount,
    isLastItemAddedUpsell
  };
};

export default withRouter(
  connect(mapStateToProps)(withLoading(CartContainer, CART))
);
